import { useState, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import type { ChannelType } from 'src/api/generated';
import { Typography } from '@material-tailwind/react';
import { type ComponentSize } from 'src/modules/shared/types/component.type';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { ConfirmModal } from 'src/modules/shared/components/organisms/modal';
import {
  isEmailChannel,
  isValidAddressForChannel,
} from 'src/modules/communications/utils/contact-address-field.util';
import { EditableAddressFieldEditor } from './editable-address-field-editor';
import { EditableAddressFieldDisplay } from './editable-address-field-display';

export interface EditableAddressFieldProps {
  address: string;
  channelType: ChannelType;
  onSave: (data: {
    address: string;
    channelType?: ChannelType;
  }) => Promise<void>;
  onDelete?: () => Promise<void>;
  onAction?: (address: string) => void;
  loading?: boolean;
  placeholder?: string;
  onCancel?: () => void;
  startEditing?: boolean;
  allowChannelTypeChange?: boolean;
  actionDisabled?: boolean;
  actionLoading?: boolean;
  actionTooltip?: string;
  size?: ComponentSize;
  className?: string;
  containerClassName?: string;
  showUtilityMenu?: boolean;
  readOnlyTooltip?: string;
}

export const EditableAddressField = ({
  address,
  channelType,
  onSave,
  onDelete,
  onAction,
  loading = false,
  placeholder,
  onCancel,
  startEditing = false,
  allowChannelTypeChange = false,
  actionDisabled = false,
  actionLoading = false,
  actionTooltip,
  size = 'sm',
  className = '',
  containerClassName = '',
  showUtilityMenu = true,
  readOnlyTooltip,
}: EditableAddressFieldProps) => {
  const { t } = useI18n();
  const isReadOnly = Boolean(readOnlyTooltip);
  const [isEditing, setIsEditing] = useState(startEditing && !isReadOnly);
  const [editedAddress, setEditedAddress] = useState(address);
  const [editedChannelType, setEditedChannelType] = useState(channelType);
  const [isCopied, setIsCopied] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const isAddressValid = isValidAddressForChannel(channelType, address);

  useEffect(() => {
    setIsEditing(startEditing && !isReadOnly);
  }, [isReadOnly, startEditing]);

  const handleSave = async () => {
    try {
      await onSave({
        address: editedAddress,
        ...(allowChannelTypeChange && { channelType: editedChannelType }),
      });
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save address:', error);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedAddress(address);
    setEditedChannelType(channelType);
    onCancel?.();
  };

  const handleDelete = async () => {
    if (!onDelete || isReadOnly) {
      return;
    }

    try {
      await onDelete();
    } catch (error) {
      console.error('Failed to delete address:', error);
    }
  };

  const handleCopy = async () => {
    if (!address) return;

    try {
      await navigator.clipboard.writeText(address);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error('Error while copying address:', error);
    }
  };

  const handleAction = () => {
    if (onAction == null || !isAddressValid || actionDisabled) return;

    if (isEmailChannel(channelType)) {
      window.location.href = `mailto:${address}`;
      return;
    }

    onAction(address);
  };

  return (
    <div className="group relative w-full">
      <div className="flex w-full items-center">
        <AnimatePresence>
          {isEditing ? (
            <EditableAddressFieldEditor
              allowChannelTypeChange={allowChannelTypeChange}
              editedAddress={editedAddress}
              editedChannelType={editedChannelType}
              loading={loading}
              onAddressChange={setEditedAddress}
              onCancel={handleCancel}
              onChannelTypeChange={setEditedChannelType}
              onSave={handleSave}
              placeholder={
                placeholder ??
                t('communications.contactPanel.contact.addresses.placeholder')
              }
              size={size}
            />
          ) : (
            <EditableAddressFieldDisplay
              actionDisabled={actionDisabled}
              actionLoading={actionLoading}
              actionTooltip={actionTooltip}
              address={address}
              channelType={channelType}
              className={className}
              containerClassName={containerClassName}
              loading={loading}
              onAction={handleAction}
              onCopy={() => void handleCopy()}
              onDelete={
                onDelete && !isReadOnly
                  ? () => setIsDeleteConfirmOpen(true)
                  : undefined
              }
              onEdit={isReadOnly ? undefined : () => setIsEditing(true)}
              readOnlyTooltip={readOnlyTooltip}
              size={size}
              showUtilityMenu={showUtilityMenu}
              utilityIsCopied={isCopied}
            />
          )}
        </AnimatePresence>
      </div>
      <ConfirmModal
        isOpen={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        title={t('shared.actions.delete')}
        variant="danger"
        confirmText={t('shared.actions.delete')}
        cancelText={t('shared.actions.cancel')}
        loading={loading}
        onConfirm={async () => {
          await handleDelete();
          setIsDeleteConfirmOpen(false);
        }}
      >
        <Typography className="text-sm text-secondary-700">
          {t('communications.contactPanel.contact.addresses.deleteConfirm')}
        </Typography>
      </ConfirmModal>
    </div>
  );
};
